import { useEffect, useRef, useState } from "react";
import { FaFileImport, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { BiSolidErrorAlt } from "react-icons/bi";
import { FileCheck, FileSpreadsheet } from "lucide-react";
import { motion } from "framer-motion";
import CalendarInline from "../../../componentes/calendar/Calendar";
import dayjs from "dayjs";
import { Dialog } from "primereact/dialog";
import Button from "../../../componentes/button/button";
import { toast } from "react-toastify";

const CONFIG = {
    greenmile: {
        title: "Greenmile",
        description: "Importar arquivo de rotas Greenmile",
        icon: FileSpreadsheet,
        borderClass: "border-green-300",
        bgClass: "bg-green-300",
        calendarColorClass: "bg-green-300",
        dotColorClass: "bg-green-300",
        buttonClass: "bg-green-500 text-white",
        iconBgClass: "bg-green-200 text-green-700",
        color: 'green'
    },

    "sap-entregas": {
        title: "Vendas SAP",
        description: "Importar arquivo de vendas do SAP",
        icon: FileSpreadsheet,
        borderClass: "border-blue-300",
        bgClass: "bg-blue-300",
        calendarColorClass: "bg-blue-300",
        dotColorClass: "bg-blue-300",
        buttonClass: "bg-blue-500 text-white",
        iconBgClass: "bg-blue-200 text-blue-700",
        color: 'blue'
    },

    "sap-retorno": {
        title: "Retorno SAP",
        description: "Importar arquivo de retorno do SAP",
        icon: FileCheck,
        borderClass: "border-orange-300",
        bgClass: "bg-orange-300",
        calendarColorClass: "bg-orange-300",
        dotColorClass: "bg-orange-300",
        buttonClass: "bg-orange-500 text-white",
        iconBgClass: "bg-orange-200 text-orange-700",
        color: 'orange'
    },

    "da-pedidos": {
        title: "Pedidos - DA",
        description: "Importar arquivo de pedidos do B.I",
        icon: FileCheck,
        borderClass: "border-indigo-300",
        bgClass: "bg-indigo-300",
        calendarColorClass: "bg-indigo-300",
        dotColorClass: "bg-indigo-300",
        buttonClass: "bg-indigo-500 text-white",
        iconBgClass: "bg-indigo-200 text-indigo-700",
        color: 'indigo'
    },
};

function formatarDuracao(ms) {
    const totalSeg = Math.max(0, Math.floor(ms / 1000));
    const min = Math.floor(totalSeg / 60);
    const seg = totalSeg % 60;
    return min > 0 ? `${min}m ${String(seg).padStart(2, "0")}s` : `${seg}s`;
}

function formatarTamanho(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_STYLE = {
    running: { box: "border-primary/30 bg-primary/5", text: "text-primary", icon: "fa fa-spinner fa-spin" },
    success: { box: "border-success/30 bg-success/5", text: "text-success", icon: "fa fa-check-circle" },
    error: { box: "border-destructive/30 bg-destructive/5", text: "text-destructive", icon: "fa fa-times-circle" },
};

const LOG_COR = {
    info: "text-muted-foreground",
    success: "text-success",
    error: "text-destructive",
};

function StatusImportacao({ status }) {
    const [agora, setAgora] = useState(Date.now());
    const [mostrarLog, setMostrarLog] = useState(false);

    const emAndamento = status.status === "running";

    useEffect(() => {
        if (!emAndamento) return;
        setAgora(Date.now());
        const id = setInterval(() => setAgora(Date.now()), 1000);
        return () => clearInterval(id);
    }, [emAndamento]);

    // abre o log automaticamente quando der erro
    useEffect(() => {
        if (status.status === "error") setMostrarLog(true);
    }, [status.status]);

    const estilo = STATUS_STYLE[status.status];
    const duracao = (status.fim ?? agora) - status.inicio;

    const textoLog = () => [
        `Arquivo: ${status.arquivo}`,
        ...status.logs.map(l => `[${dayjs(l.hora).format("HH:mm:ss")}] ${l.msg}`),
        status.erro?.detalhes ? `\n${status.erro.detalhes}` : null,
    ].filter(Boolean).join("\n");

    const copiarLog = () => {
        navigator.clipboard?.writeText(textoLog())
            .then(() => toast.info("Log copiado.", { autoClose: 1500 }))
            .catch(() => { });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-3 rounded-lg border px-3 py-2.5 text-xs ${estilo.box}`}
        >
            <div className="flex items-center gap-2">
                <i className="fa fa-file-alt text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-semibold text-foreground" title={status.arquivo}>
                    {status.arquivo}
                </span>
                <span className="text-[10px] text-muted-foreground">{formatarTamanho(status.tamanho)}</span>
            </div>

            <div className="mt-1.5 flex items-center gap-2">
                <i className={`${estilo.icon} ${estilo.text}`} />
                <span className={`flex-1 font-medium ${estilo.text}`}>
                    {status.etapa}
                    {emAndamento && status.detalhe && (
                        <span className="ml-1 font-normal text-muted-foreground tabular-nums">{status.detalhe}</span>
                    )}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 font-mono text-[10px] font-semibold text-foreground tabular-nums" title="Tempo decorrido">
                    <i className="fa fa-stopwatch text-muted-foreground" />
                    {formatarDuracao(duracao)}
                </span>
            </div>

            {emAndamento && status.percentual != null && (
                <div className="mt-2">
                    <div className="h-1.5 overflow-hidden rounded-full bg-primary/10">
                        <div
                            className="h-full rounded-full bg-primary transition-all duration-500"
                            style={{ width: `${status.percentual}%` }}
                        />
                    </div>
                    <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted-foreground">
                        <span>{status.percentual}%</span>
                        {status.restante != null && <span>restam ~{formatarDuracao(status.restante * 1000)}</span>}
                    </div>
                </div>
            )}

            {emAndamento && status.percentual == null && (
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-primary/10">
                    <motion.div
                        className="h-full w-1/3 rounded-full bg-primary"
                        animate={{ x: ["-100%", "300%"] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>
            )}

            {status.status === "error" && (
                <p className="mt-2 break-words rounded-md bg-destructive/10 px-2 py-1.5 font-semibold text-destructive">
                    {status.erro?.mensagem}
                </p>
            )}

            <div className="mt-2 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setMostrarLog(v => !v)}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                >
                    <i className={`fa fa-chevron-${mostrarLog ? "up" : "down"} text-[9px]`} />
                    {mostrarLog ? "Ocultar log" : "Ver log"}
                </button>
                {mostrarLog && (
                    <button
                        type="button"
                        onClick={copiarLog}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                        <i className="fa fa-copy text-[9px]" />
                        Copiar
                    </button>
                )}
            </div>

            {mostrarLog && (
                <div className="mt-1.5 max-h-48 overflow-auto rounded-md border border-border bg-card p-2 font-mono text-[10px] leading-relaxed">
                    {status.logs.map((l, i) => (
                        <div key={i} className={LOG_COR[l.tipo]}>
                            <span className="opacity-60">[{dayjs(l.hora).format("HH:mm:ss")}]</span> {l.msg}
                        </div>
                    ))}
                    {status.erro?.detalhes && (
                        <pre className="mt-1.5 whitespace-pre-wrap break-words border-t border-border pt-1.5 text-destructive/80">
                            {status.erro.detalhes}
                        </pre>
                    )}
                </div>
            )}
        </motion.div>
    );
}

const ImportCard = ({ type, onImport, service, responseResult, loading, status, abrirGerenciamento, showGerenciamento = false, accept = '.csv' }) => {
    const fileInputRef = useRef(null);
    const config = CONFIG[type];
    const Icon = config.icon;

    const [data, setData] = useState(null);
    const [mes, setMes] = useState(dayjs().format("YYYY-MM"));
    const [highlightDates, setHighlightDates] = useState([]);
    const [abrirModalErros, setAbrirModalErros] = useState(false);
    const [abrirModalAlertas, setAbrirModalAlertas] = useState(false);

    useEffect(() => {
        if (service) {
            buscarDiasDoMes();
        }
    }, [mes]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            onImport(type, file);
            e.target.value = "";
        }
    };

    const buscarDiasDoMes = async () => {
        const inicio = dayjs(mes + "-01").startOf("month").format("YYYY-MM-DD");
        const fim = dayjs(mes + "-01").endOf("month").format("YYYY-MM-DD");

        const dias = await service?.listarOperacoesPorPeriodo(inicio, fim);

        setHighlightDates(dias.data);
    };

    const Item = ({ icon: Icon, numero, label, cor, onClick }) => (
        <div className="flex flex-col items-center mx-2 hover:scale-105 transition-transform duration-150 cursor-pointer" onClick={onClick}>
            <span className="text-sm font-bold text-foreground">{numero}</span>
            <Icon className={`text-md mt-1 ${cor}`} />
            <span className="text-xs mt-1 text-muted-foreground">{label}</span>
        </div>
    );

    const baixarTxt = (type) => {
        const conteudo = responseResult[type].join("\n");
        const blob = new Blob([conteudo], { type: "text/plain;charset=utf-8;" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${type}-importacao-${new Date().toISOString().slice(0, 19)}.txt`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`rounded-xl border-2 bg-card p-5 shadow-card hover:shadow-soft transition-shadow duration-150 ${config.borderClass} `}
        >
            <div className="flex items-start justify-between mb-4">

                <div className="flex items-start gap-3">

                    <div className={`rounded-lg p-2.5 ${config.iconBgClass}`}>
                        <Icon className="h-5 w-5" />
                    </div>

                    <div>
                        <h3 className="font-semibold">{config.title}</h3>
                        <p className="text-xs text-muted-foreground">
                            {config.description}
                        </p>
                    </div>

                </div>

                {showGerenciamento &&
                    <button
                        type="button"
                        onClick={() => abrirGerenciamento(type)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-primary"
                        title="Gerenciar registros"
                    >
                        <i className="fa fa-sliders-h" />
                    </button>
                }

            </div>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={accept}
                onChange={handleFileChange}
            />

            <Button
                label="Importar Arquivo"
                iconLeft="fa fa-upload"
                disabled={loading}
                className={`w-full gap-2 py-3 font-medium rounded-lg ${config.buttonClass} justify-center disabled:cursor-not-allowed`}
                variant={config.color}
                rounded="lg"
                onClick={() => fileInputRef.current?.click()}
            />

            {status && <StatusImportacao key={status.inicio} status={status} />}

            {(responseResult) &&
                <div className="mt-3 flex justify-center">
                    <Item
                        icon={FaFileImport}
                        numero={responseResult.totalProcessadas}
                        label="Processadas"
                        cor="text-blue-500"
                    />

                    <Item
                        icon={FaCheckCircle}
                        numero={responseResult.totalImportadas}
                        label="Importadas"
                        cor="text-success"
                    />

                    <Item
                        icon={FaExclamationTriangle}
                        numero={responseResult?.alertas?.length ?? 0}
                        label="Alertas"
                        cor="text-amber-500"
                        onClick={() => setAbrirModalAlertas(true)}
                    />

                    <Item
                        icon={BiSolidErrorAlt}
                        numero={responseResult.totalIgnoradas}
                        label="Erros"
                        cor="text-destructive"
                        onClick={() => setAbrirModalErros(true)}
                    />
                </div>
            }
            <div className="mt-3"></div>
            <CalendarInline
                value={data}
                onChange={(date) => setData(date)}
                highlightDates={highlightDates}
                onMonthChange={(mes) => setMes(mes)}
                color={config.color}
            />

            <Dialog header="Erros de Importação"
                visible={abrirModalErros}
                blockScroll
                className="w-[80%]"
                contentClassName="p-0"
                onHide={() => setAbrirModalErros(false)}
                footer={
                    <div className="border-t border-border p-3 flex justify-between">
                        <Button
                            label="Voltar"
                            onClick={() => setAbrirModalErros(false)}
                            iconLeft="fa fa-arrow-left"
                            variant="gray"
                        />
                        <Button label="Fazer Download"
                            iconLeft="fa fa-download"
                            variant="white"
                            onClick={() => baixarTxt("erros")}
                        />
                    </div>
                }
            >
                <div className="p-3 py-1 overflow-auto text-sm bg-destructive/10 m-3">
                    {responseResult?.erros.map((erro, i) => (
                        <div
                            key={i}
                            className="font-semibold p-2 rounded"
                        >
                            {erro}
                        </div>
                    ))}
                </div>

                {/* FOOTER */}

            </Dialog>

            <Dialog header="Alertas de Importação"
                visible={abrirModalAlertas}
                blockScroll
                onHide={() => setAbrirModalAlertas(false)}
                className="w-[80%]"
                contentClassName="p-0"
                footer={
                    <div className="border-t border-border p-3 flex justify-between">
                        <Button
                            label="Voltar"
                            onClick={() => setAbrirModalAlertas(false)}
                            iconLeft="fa fa-arrow-left"
                            variant="gray"
                        />
                        <Button label="Fazer Download"
                            iconLeft="fa fa-download"
                            variant="white"
                            onClick={() => baixarTxt("alertas")}
                        />
                    </div>
                }
            >
                <div className="p-3 py-1 overflow-auto text-sm bg-amber-50 m-3">
                    {responseResult?.alertas.map((alerta, i) => (
                        <div
                            key={i}
                            className="font-semibold p-2 rounded"
                        >
                            {alerta}
                        </div>
                    ))}
                </div>


            </Dialog>

        </motion.div>
    );
};

export default ImportCard;
